import React, { memo } from 'react'
import styled from 'styled-components/macro'
import CircularProgress from '@alobato/circular-progress'
import useFetch from '@alobato/use-fetch'

import { CloseIcon } from '../../components/Icons'

import DEFAULT_HEADERS from '../../constants/defaultHeaders'
import BASE_API from '../../constants/baseApi'
import AUTH_TOKEN from '../../constants/authToken'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 860px;
  height: 100%;
`

/* sticky (não fixed): o header acompanha o scroll DENTRO do modal.
   Com position:fixed ele se ancorava à viewport e cobria o conteúdo. */
const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 0 8px 0 24px;
  background: #f7f9fc;
  border-bottom: 1px solid #e6eaf0;
`

const Title = styled.div`
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  color: hsla(199, 75%, 47%, 1);
`

const CloseButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease;
  &:hover { background: rgba(0, 0, 0, 0.06); }
`

const Body = styled.div`
  flex: 1;
  padding: 24px;
  overflow: auto;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
  margin-bottom: 28px;
`

const Field = styled.div`
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 10px 14px;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #90a4bd;
  margin-bottom: 4px;
`

const FieldValue = styled.div`
  font-size: 14px;
  color: #26364a;
  word-break: break-word;
  ${props => props.$mono && 'font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 13px;'}
`

const STATUS_COLORS = {
  'PENDENTE': ['#fff4e0', '#a86a12'],
  'CONCLUÍDA': ['#e6f6ec', '#1d7a45'],
  'CONCLUÍDA COM ERRO': ['#fdeaea', '#b3261e'],
  'ERRO': ['#fdeaea', '#b3261e']
}

const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: ${props => (STATUS_COLORS[props.$status] || ['#eef2f7', '#5b6b80'])[0]};
  color: ${props => (STATUS_COLORS[props.$status] || ['#eef2f7', '#5b6b80'])[1]};
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7f99;
  margin-bottom: 12px;
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e6eaf0;
  }
`

const Counter = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2f7;
  color: #5b6b80;
  font-size: 11px;
`

const ExamCard = styled.div`
  border: 1px solid #e6eaf0;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: #fff;
`

const ExamName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #26364a;
  margin-bottom: 2px;
`

const ExamDate = styled.div`
  font-size: 12px;
  color: #90a4bd;
  margin-bottom: 10px;
`

const MediaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 8px 8px 0;
  padding: 7px 12px;
  border-radius: 6px;
  background: #eef6fc;
  color: #1f7bb6;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s ease;
  &:hover { background: #dceefa; }
`

const Empty = styled.div`
  padding: 20px;
  text-align: center;
  color: #9db0c7;
  font-size: 14px;
  background: #f8fafc;
  border-radius: 8px;
`

const formatDateTime = value => {
  if (!value) return '—'
  const d = new Date(value)
  if (isNaN(d)) return value
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const fileNameFromUrl = url => {
  if (!url) return 'arquivo'
  const path = url.split('?')[0]
  const name = path.substring(path.lastIndexOf('/') + 1)
  try { return decodeURIComponent(name) || 'arquivo' } catch (e) { return name || 'arquivo' }
}

const fileKind = url => {
  const name = fileNameFromUrl(url).toLowerCase()
  if (name.endsWith('.pdf')) return 'PDF'
  if (/\.(png|jpe?g|gif|webp|bmp)$/.test(name)) return 'Imagem'
  return 'Arquivo'
}

/* URLs absolutas do S3 já vêm assinadas (AWS_QUERYSTRING_AUTH);
   anexar ?token= quebraria a assinatura. O token só é usado para
   mídias servidas pela própria API. */
const buildMediaHref = url => {
  if (!url) return '#'
  const isExternal = /^https?:\/\//i.test(url) && url.indexOf(BASE_API) !== 0
  if (isExternal) return url
  const separator = url.indexOf('?') > -1 ? '&' : '?'
  return `${url}${separator}token=token%20${localStorage.getItem(AUTH_TOKEN)}`
}

const Show = memo(({ data, onRequestClose }) => {
  const establishment = (data.establishment && data.establishment.name) || data.establishment_name || '—'
  const patientEmail = (data.user && data.user.login) || '—'
  const exams = data.exams || []

  return (
    <Wrapper>
      <Header>
        <Title>Solicitação #{data.id}</Title>
        <CloseButton onClick={onRequestClose}><CloseIcon /></CloseButton>
      </Header>

      <Body>
        <SectionTitle>Solicitação</SectionTitle>
        <Grid>
          <Field>
            <FieldLabel>Status</FieldLabel>
            <FieldValue><Badge $status={data.status}>{data.status || '—'}</Badge></FieldValue>
          </Field>
          <Field>
            <FieldLabel>Data/Hora</FieldLabel>
            <FieldValue>{formatDateTime(data.created_at)}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Estabelecimento</FieldLabel>
            <FieldValue>{establishment}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Cidade</FieldLabel>
            <FieldValue>{data.city || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Paciente (e-mail)</FieldLabel>
            <FieldValue>{patientEmail}</FieldValue>
          </Field>
          {data.error &&
            <Field $full>
              <FieldLabel>Erro</FieldLabel>
              <FieldValue>{data.error}</FieldValue>
            </Field>
          }
        </Grid>

        <SectionTitle>Credenciais do laboratório</SectionTitle>
        <Grid>
          <Field>
            <FieldLabel>Login</FieldLabel>
            <FieldValue $mono>{data.login || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Senha</FieldLabel>
            <FieldValue $mono>{data.password || '—'}</FieldValue>
          </Field>
        </Grid>

        <SectionTitle>Exames <Counter>{exams.length}</Counter></SectionTitle>
        {exams.length === 0
          ? <Empty>Nenhum exame vinculado a esta solicitação.</Empty>
          : exams.map(exam => (
            <ExamCard key={exam.id}>
              <ExamName>{exam.name || 'Exame sem nome'}</ExamName>
              <ExamDate>Data do exame: {exam.formated_date || '—'}</ExamDate>
              {(exam.medias || []).length === 0
                ? <Empty>Sem arquivos anexados.</Empty>
                : exam.medias.map(media => (
                  <MediaLink
                    key={media.id}
                    href={buildMediaHref(media.image)}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={fileNameFromUrl(media.image)}
                  >
                    {fileKind(media.image)} · {fileNameFromUrl(media.image)}
                  </MediaLink>
                ))
              }
            </ExamCard>
          ))
        }
      </Body>
    </Wrapper>
  )
})

export default memo(props => {
  const { data, error, loading } = useFetch(`${BASE_API}/solicitation/${props.id}/`, { headers: DEFAULT_HEADERS })
  if (error) return `Erro! ${error.message}`
  if (!data || loading) return <CircularProgress color='hsla(199, 75%, 43%, 1)' />
  return <Show {...props} data={data} />
})
